import { useState } from "react";
import { Box, Typography, Avatar, Button, Dialog, TextField, IconButton, InputAdornment, Menu, MenuItem, Tooltip, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../Components/SnackBar";
import { managersHubMock, searchableUsers, getTotals, ITEM_TYPES, ITEM_CATEGORIES } from "../mockData";

const GREEN = "#248B70";
const TEXT_MAIN = "rgba(47, 43, 61, 0.9)";
const TEXT_SUB = "rgba(71, 84, 103, 1)";
const CARD = { backgroundColor: "#fff", border: "1px solid rgba(234,234,234,1)", borderRadius: "12px" };
const primaryBtn = { textTransform: "none", fontSize: "14px", fontWeight: 600, color: "#fff", backgroundColor: GREEN, borderRadius: "8px", padding: "8px 18px", boxShadow: "none", "&:hover": { backgroundColor: "rgba(30,118,95,1)", boxShadow: "none" } };
const chip = { display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: "6px", backgroundColor: "#F2F3FB", fontSize: "12px", fontWeight: 500, color: TEXT_MAIN, whiteSpace: "nowrap" };
// Fractional columns so the table fills the content width like the app's other
// tables (no fixed min-width that overflows the panel).
const COLS = "minmax(0,1.7fr) minmax(0,1.3fr) minmax(0,0.7fr) minmax(0,0.7fr) minmax(0,1.8fr) minmax(0,1fr) minmax(0,0.8fr) minmax(0,0.95fr) 44px";
const ROW = { display: "grid", gridTemplateColumns: COLS, gap: "12px", alignItems: "center", padding: "14px 18px" };

const CommunitiesTable = () => {
  const { t, i18n } = useTranslation("managers-hub");
  const showSnackbar = useSnackbar();
  const isMobile = useMediaQuery("(max-width:767px)");
  const lang = (i18n.language || "he").toLowerCase().startsWith("he") ? "he" : "en";
  const loc = (v) => (v && typeof v === "object" ? v[lang] ?? v.en : v);
  const m = managersHubMock;
  const cur = m.currencySymbol;
  const money = (n) => `${cur}${Number(n).toLocaleString("en-US")}`;
  const clamp = (v) => Math.max(0, Math.min(100, Number(v)));

  const [cuts, setCuts] = useState(Object.fromEntries(m.communities.map((c) => [c.id, c.cutPercent])));
  const cutOf = (c) => cuts[c.id];
  const cutMoney = (c) => Math.round((c.incomeLastMonth * cutOf(c)) / 100);
  const totals = getTotals(m, cutOf);

  // Per-community cut display + edit (via the ⋮ menu, not an inline input).
  const [editCutFor, setEditCutFor] = useState(null);
  const [editCutValue, setEditCutValue] = useState(0);
  const openEditCut = (c) => { setEditCutFor(c); setEditCutValue(cutOf(c)); };
  const saveEditCut = () => { setCuts((o) => ({ ...o, [editCutFor.id]: clamp(editCutValue) })); setEditCutFor(null); };

  // "Your cut" cell/value — money with the % in parentheses.
  const YourCut = ({ c }) => (
    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: GREEN, whiteSpace: "nowrap" }}>
      {money(cutMoney(c))} ({cutOf(c)}%)
    </Typography>
  );

  // Label for the totals "your cut" with a (?) explaining how the cut is made.
  const TotalCutLabel = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <Typography sx={{ fontSize: "11px", color: TEXT_SUB }}>{t("your_total_cut")}</Typography>
      <Tooltip title={t("cut_help")} arrow>
        <HelpOutlineIcon sx={{ fontSize: 14, color: TEXT_SUB, cursor: "help" }} />
      </Tooltip>
    </Box>
  );

  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastSent, setBroadcastSent] = useState(false);
  const [replaceFor, setReplaceFor] = useState(null);
  const [replaceSent, setReplaceSent] = useState(false);
  const [query, setQuery] = useState("");
  const [invite, setInvite] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [messageFor, setMessageFor] = useState(null);
  const [messageSent, setMessageSent] = useState(false);

  const openMenu = (e, c) => { setMenuAnchor(e.currentTarget); setMenuFor(c); };
  const closeMenu = () => { setMenuAnchor(null); setMenuFor(null); };
  const copyLink = (kind, c) => {
    // Mock invite links — the real signed links come from the backend.
    const url = `https://app.keilot.com/invite/${kind}/${c.id}`;
    navigator.clipboard?.writeText(url);
    showSnackbar(t("link_copied"), "success");
    closeMenu();
  };

  const openReplace = (c) => { setReplaceFor(c); setReplaceSent(false); setQuery(""); setInvite(""); };
  const results = query.trim()
    ? searchableUsers.filter((u) => loc(u.name).includes(query.trim()) || u.subtitle.includes(query.trim()))
    : [];

  const ranked = [...m.communities].sort((a, b) => b.incomeLastMonth - a.incomeLastMonth);

  const ItemChips = ({ items, plain }) => {
    const types = ITEM_TYPES.filter((tp) => (items?.[tp] || 0) > 0);
    // Totals row: plain text, no pill/tag look.
    if (plain) {
      return (
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: TEXT_MAIN }}>
          {types.map((tp) => `${t(`item_types.${tp}`, { defaultValue: tp })} (${items[tp]})`).join(", ")}
        </Typography>
      );
    }
    // Community rows: chips, each with a tooltip listing that type's categories.
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {types.map((tp) => (
          <Tooltip key={tp} title={(ITEM_CATEGORIES[tp] || []).join(", ")} arrow>
            <Box sx={chip}>{t(`item_types.${tp}`, { defaultValue: tp })} ({items[tp]})</Box>
          </Tooltip>
        ))}
      </Box>
    );
  };

  const Metric = ({ label, value, help }) => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Typography sx={{ fontSize: "12px", color: TEXT_SUB }}>{label}</Typography>
        {help && (
          <Tooltip title={help} arrow>
            <HelpOutlineIcon sx={{ fontSize: 13, color: TEXT_SUB, cursor: "help" }} />
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: TEXT_MAIN }}>{value}</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: { xs: "4px", md: 0 } }}>
        <Button startIcon={<CampaignOutlinedIcon />} onClick={() => { setBroadcastOpen(true); setBroadcastSent(false); }} sx={{ ...primaryBtn, width: { xs: "100%", md: "auto" } }}>
          {t("broadcast")}
        </Button>
      </Box>

      {/* Mobile: stacked cards (the desktop table's 9 columns don't fit a phone) */}
      {isMobile && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Box sx={{ ...CARD, backgroundColor: "rgba(242,243,251,0.6)", padding: "14px 16px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TEXT_MAIN, mb: "10px" }}>
              {t("totals_label")} · {t("totals_communities", { count: totals.communities })}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
              <Metric label={t("col_members")} value={totals.members} />
              <Metric label={t("col_suppliers")} value={totals.suppliers} />
              <Metric label={t("col_transactions")} value={totals.transactions} />
            </Box>
            <Box sx={{ mt: "12px" }}>
              <Metric label={t("col_income")} value={money(totals.incomeLastMonth)} help={t("income_help")} />
            </Box>
            <Box sx={{ mt: "12px" }}>
              <Typography sx={{ fontSize: "12px", color: TEXT_SUB, mb: "4px" }}>{t("col_items")}</Typography>
              <ItemChips items={totals.items} plain />
            </Box>
            <Box sx={{ mt: "12px" }}>
              <TotalCutLabel />
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: GREEN }}>{money(totals.myCut)}</Typography>
            </Box>
          </Box>

          {ranked.map((c) => (
            <Box key={c.id} sx={{ ...CARD, padding: "14px 16px" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Avatar src={c.image} sx={{ width: 36, height: 36 }} />
                  <Box>
                    <Typography sx={{ fontSize: "15px", fontWeight: 600, color: TEXT_MAIN }}>{loc(c.name)}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "2px" }}>
                      <Avatar src={c.cm.avatar} sx={{ width: 18, height: 18 }} />
                      <Typography sx={{ fontSize: "12px", color: TEXT_SUB }}>{loc(c.cm.name)}</Typography>
                    </Box>
                  </Box>
                </Box>
                <IconButton size="small" aria-label="actions" onClick={(e) => openMenu(e, c)} sx={{ color: TEXT_SUB }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", mt: "14px" }}>
                <Metric label={t("col_members")} value={c.members} />
                <Metric label={t("col_suppliers")} value={c.suppliers} />
                <Metric label={t("col_transactions")} value={c.transactions} />
              </Box>

              <Box sx={{ mt: "12px" }}>
                <Metric label={t("col_income")} value={money(c.incomeLastMonth)} help={t("income_help")} />
              </Box>

              <Box sx={{ mt: "12px" }}>
                <Typography sx={{ fontSize: "12px", color: TEXT_SUB, mb: "6px" }}>{t("col_items")}</Typography>
                <ItemChips items={c.items} />
              </Box>

              <Box sx={{ mt: "12px" }}>
                <Typography sx={{ fontSize: "12px", color: TEXT_SUB, mb: "2px" }}>{t("col_your_cut")}</Typography>
                <YourCut c={c} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {!isMobile && (
      <Box sx={{ ...CARD, overflowX: "auto" }}>
        <Box>
          {/* Column header */}
          <Box sx={{ ...ROW, backgroundColor: "#f9fafb", borderBottom: "1px solid rgba(234,234,234,1)", py: "12px" }}>
            {["community", "manager", "members", "suppliers", "items", "income", "transactions", "your_cut"].map((h) =>
              h === "income" ? (
                <Box key={h} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: TEXT_SUB }}>{t("col_income")}</Typography>
                  <Tooltip title={t("income_help")} arrow>
                    <HelpOutlineIcon sx={{ fontSize: 13, color: TEXT_SUB, cursor: "help" }} />
                  </Tooltip>
                </Box>
              ) : (
                <Typography key={h} sx={{ fontSize: "12px", fontWeight: 600, color: TEXT_SUB }}>{t(`col_${h}`)}</Typography>
              )
            )}
            <Box />
          </Box>

          {/* Totals row */}
          <Box sx={{ ...ROW, backgroundColor: "rgba(242,243,251,0.6)", borderBottom: "1px solid rgba(234,234,234,1)" }}>
            <Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TEXT_MAIN }}>{t("totals_label")}</Typography>
              <Typography sx={{ fontSize: "12px", color: TEXT_SUB }}>{t("totals_communities", { count: totals.communities })}</Typography>
            </Box>
            <Box />
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TEXT_MAIN }}>{totals.members}</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TEXT_MAIN }}>{totals.suppliers}</Typography>
            <ItemChips items={totals.items} plain />
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TEXT_MAIN }}>{money(totals.incomeLastMonth)}</Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: TEXT_MAIN }}>{totals.transactions}</Typography>
            <Box>
              <TotalCutLabel />
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: GREEN }}>{money(totals.myCut)}</Typography>
            </Box>
            <Box />
          </Box>

          {/* Per-community rows */}
          {ranked.map((c) => (
            <Box key={c.id} sx={{ ...ROW, borderBottom: "1px solid rgba(240,240,240,1)" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Avatar src={c.image} sx={{ width: 32, height: 32 }} />
                <Box>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: TEXT_MAIN }}>{loc(c.name)}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Avatar src={c.cm.avatar} sx={{ width: 28, height: 28 }} />
                <Typography sx={{ fontSize: "13px", color: TEXT_SUB }}>{loc(c.cm.name)}</Typography>
              </Box>
              <Typography sx={{ fontSize: "13px", color: TEXT_MAIN }}>{c.members}</Typography>
              <Typography sx={{ fontSize: "13px", color: TEXT_MAIN }}>{c.suppliers}</Typography>
              <ItemChips items={c.items} />
              <Typography sx={{ fontSize: "14px", color: TEXT_MAIN }}>{money(c.incomeLastMonth)}</Typography>
              <Typography sx={{ fontSize: "13px", color: TEXT_MAIN }}>{c.transactions}</Typography>
              <YourCut c={c} />
              <IconButton size="small" aria-label="actions" onClick={(e) => openMenu(e, c)} sx={{ color: TEXT_SUB }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
      )}

      {/* Row actions menu (3-dots) */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: "10px", boxShadow: "0px 4px 16px rgba(0,0,0,0.12)", minWidth: "180px" } }}>
        <MenuItem onClick={() => { const c = menuFor; closeMenu(); setMessageFor(c); setMessageSent(false); }} sx={{ fontSize: "14px" }}>
          {t("message_manager")}
        </MenuItem>
        <MenuItem onClick={() => copyLink("members", menuFor)} sx={{ fontSize: "14px" }}>
          {t("copy_member_link")}
        </MenuItem>
        <MenuItem onClick={() => copyLink("suppliers", menuFor)} sx={{ fontSize: "14px" }}>
          {t("copy_supplier_link")}
        </MenuItem>
        <MenuItem onClick={() => { const c = menuFor; closeMenu(); openEditCut(c); }} sx={{ fontSize: "14px" }}>
          {t("edit_cut_menu")}
        </MenuItem>
        <MenuItem onClick={() => { const c = menuFor; closeMenu(); openReplace(c); }} sx={{ fontSize: "14px" }}>
          {t("replace")}
        </MenuItem>
        {/* Only for communities the MoM also manages as CM — he switches role to
            edit as the CM (no read-only "view mode"). */}
        {menuFor?.selfManaged && (
          <MenuItem onClick={closeMenu} sx={{ fontSize: "14px" }}>
            {t("switch_to_community")}
          </MenuItem>
        )}
      </Menu>

      {/* Edit this community's cut % */}
      <Dialog open={!!editCutFor} onClose={() => setEditCutFor(null)} PaperProps={{ sx: { borderRadius: "16px", width: "400px", maxWidth: "calc(100% - 32px)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid rgba(234,234,234,1)" }}>
          <Typography sx={{ fontSize: "17px", fontWeight: 600, color: TEXT_MAIN }}>{t("edit_cut_title")}</Typography>
          <IconButton onClick={() => setEditCutFor(null)}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <Typography sx={{ fontSize: "13px", color: TEXT_SUB }}>{t("edit_cut_sub", { community: editCutFor ? loc(editCutFor.name) : "" })}</Typography>
          <TextField type="number" value={editCutValue} onChange={(e) => setEditCutValue(clamp(e.target.value))} autoFocus
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            sx={{ width: "140px", "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "18px", fontWeight: 600 } }} />
          <Button onClick={saveEditCut} sx={{ ...primaryBtn, alignSelf: "flex-start" }}>{t("edit_cut_save")}</Button>
        </Box>
      </Dialog>

      {/* Message one CM — same idea as broadcast, to a single manager */}
      <Dialog open={!!messageFor} onClose={() => setMessageFor(null)} PaperProps={{ sx: { borderRadius: "16px", width: "460px", maxWidth: "calc(100% - 32px)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid rgba(234,234,234,1)" }}>
          <Typography sx={{ fontSize: "17px", fontWeight: 600, color: TEXT_MAIN }}>{messageSent ? t("message_sent_title") : t("message_title")}</Typography>
          <IconButton onClick={() => setMessageFor(null)}><CloseIcon /></IconButton>
        </Box>
        {messageSent ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "28px 20px", textAlign: "center" }}>
            <Box sx={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "rgba(219,240,236,1)", color: GREEN, display: "flex", alignItems: "center", justifyContent: "center" }}><CheckCircleOutlineIcon /></Box>
            <Typography sx={{ fontSize: "14px", color: TEXT_MAIN }}>{t("message_sent_sub", { name: messageFor ? loc(messageFor.cm.name) : "" })}</Typography>
            <Button onClick={() => setMessageFor(null)} sx={primaryBtn}>{t("done")}</Button>
          </Box>
        ) : (
          <Box sx={{ padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography sx={{ fontSize: "13px", color: TEXT_SUB }}>{t("message_sub", { name: messageFor ? loc(messageFor.cm.name) : "", community: messageFor ? loc(messageFor.name) : "" })}</Typography>
            <TextField placeholder={t("message_placeholder")} multiline minRows={4} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "14px" } }} />
            <Button onClick={() => setMessageSent(true)} fullWidth sx={primaryBtn}>{t("message_send")}</Button>
          </Box>
        )}
      </Dialog>

      {/* Broadcast modal */}
      <Dialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} PaperProps={{ sx: { borderRadius: "16px", width: "460px", maxWidth: "calc(100% - 32px)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid rgba(234,234,234,1)" }}>
          <Typography sx={{ fontSize: "17px", fontWeight: 600, color: TEXT_MAIN }}>{broadcastSent ? t("broadcast_sent_title") : t("broadcast_title")}</Typography>
          <IconButton onClick={() => setBroadcastOpen(false)}><CloseIcon /></IconButton>
        </Box>
        {broadcastSent ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "28px 20px", textAlign: "center" }}>
            <Box sx={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "rgba(219,240,236,1)", color: GREEN, display: "flex", alignItems: "center", justifyContent: "center" }}><CheckCircleOutlineIcon /></Box>
            <Typography sx={{ fontSize: "14px", color: TEXT_MAIN }}>{t("broadcast_sent_sub", { count: m.communities.length })}</Typography>
            <Button onClick={() => setBroadcastOpen(false)} sx={primaryBtn}>{t("done")}</Button>
          </Box>
        ) : (
          <Box sx={{ padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography sx={{ fontSize: "13px", color: TEXT_SUB }}>{t("broadcast_sub", { count: m.communities.length })}</Typography>
            <TextField placeholder={t("broadcast_placeholder")} multiline minRows={4} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "14px" } }} />
            <Button onClick={() => setBroadcastSent(true)} fullWidth sx={primaryBtn}>{t("broadcast_send")}</Button>
          </Box>
        )}
      </Dialog>

      {/* Replace manager modal — search existing user OR invite new */}
      <Dialog open={!!replaceFor} onClose={() => setReplaceFor(null)} PaperProps={{ sx: { borderRadius: "16px", width: "460px", maxWidth: "calc(100% - 32px)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid rgba(234,234,234,1)" }}>
          <Typography sx={{ fontSize: "17px", fontWeight: 600, color: TEXT_MAIN }}>{replaceSent ? t("replace_sent_title") : t("replace_title")}</Typography>
          <IconButton onClick={() => setReplaceFor(null)}><CloseIcon /></IconButton>
        </Box>
        {replaceSent ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "28px 20px", textAlign: "center" }}>
            <Box sx={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "rgba(219,240,236,1)", color: GREEN, display: "flex", alignItems: "center", justifyContent: "center" }}><CheckCircleOutlineIcon /></Box>
            <Typography sx={{ fontSize: "14px", color: TEXT_MAIN, maxWidth: "300px" }}>{t("replace_sent_sub")}</Typography>
            <Button onClick={() => setReplaceFor(null)} sx={primaryBtn}>{t("done")}</Button>
          </Box>
        ) : (
          <Box sx={{ padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
            <Box sx={{ backgroundColor: "rgba(242,243,251,0.6)", borderRadius: "10px", padding: "10px 12px" }}>
              <Typography sx={{ fontSize: "12px", color: TEXT_SUB }}>{t("replace_community")}</Typography>
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: TEXT_MAIN }}>{replaceFor && loc(replaceFor.name)}</Typography>
              <Typography sx={{ fontSize: "12px", color: TEXT_SUB, mt: "4px" }}>{t("replace_current", { name: replaceFor ? loc(replaceFor.cm.name) : "" })}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: TEXT_MAIN, mb: "6px" }}>{t("replace_search_label")}</Typography>
              <TextField value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("replace_search_placeholder")} fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: TEXT_SUB }} /></InputAdornment> }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "14px" } }} />
              {query.trim() && (
                <Box sx={{ mt: "8px", border: "1px solid rgba(234,234,234,1)", borderRadius: "10px", overflow: "hidden", maxHeight: "180px", overflowY: "auto" }}>
                  {results.length ? results.map((u) => (
                    <Box key={u.id} onClick={() => setReplaceSent(true)} sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", cursor: "pointer", "&:hover": { backgroundColor: "rgba(242,243,251,0.7)" } }}>
                      <Avatar src={u.avatar} sx={{ width: 30, height: 30 }} />
                      <Box>
                        <Typography sx={{ fontSize: "14px", color: TEXT_MAIN }}>{loc(u.name)}</Typography>
                        <Typography sx={{ fontSize: "12px", color: TEXT_SUB }}>{u.subtitle}</Typography>
                      </Box>
                    </Box>
                  )) : <Typography sx={{ fontSize: "13px", color: TEXT_SUB, padding: "12px" }}>{t("replace_search_empty")}</Typography>}
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Box sx={{ flex: 1, borderTop: "1px solid rgba(234,234,234,1)" }} />
              <Typography sx={{ fontSize: "12px", color: TEXT_SUB }}>{t("replace_or")}</Typography>
              <Box sx={{ flex: 1, borderTop: "1px solid rgba(234,234,234,1)" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: TEXT_MAIN, mb: "6px" }}>{t("replace_invite_label")}</Typography>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <TextField value={invite} onChange={(e) => setInvite(e.target.value)} placeholder={t("replace_invite_placeholder")} fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "14px" } }} />
                <Button onClick={() => invite.trim() && setReplaceSent(true)} disabled={!invite.trim()}
                  sx={{ ...primaryBtn, whiteSpace: "nowrap", "&.Mui-disabled": { backgroundColor: "rgba(36,139,112,0.35)", color: "#fff" } }}>
                  {t("replace_send")}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default CommunitiesTable;
